import { useState } from "react";
import { Check, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Badge } from "@/components/ui/badge";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent, CLIENT_EVENTS, DEFAULT_CLIENT_EVENTS } from "@/types/agent/agent";

interface ClientEventsCardProps {
  agent: Agent;
  brand?: string;
}

export const ClientEventsCard = ({ agent, brand = "#4E9CFF" }: ClientEventsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const getEnabledEvents = (clientEvents: string[]): string[] => {
    if (clientEvents.length > 0) return clientEvents;
    return DEFAULT_CLIENT_EVENTS as unknown as string[];
  };

  const rawEvents: string[] = agent.settings?.conversation_config?.conversation?.client_events ?? [];
  const [selectedEvents, setSelectedEvents] = useState<string[]>(rawEvents);

  const updateAgent = useUpdateAgent();

  const handleEdit = () => {
    setSelectedEvents(agent.settings?.conversation_config?.conversation?.client_events ?? []);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        settings: {
          ...agent.settings,
          conversation_config: {
            ...agent.settings?.conversation_config,
            conversation: {
              ...agent.settings?.conversation_config?.conversation,
              client_events: selectedEvents,
            },
          },
        },
      },
    });
    toast.success("Client events saved");
    setIsEditing(false);
  };

  const toggleEvent = (value: string, checked: boolean) => {
    setSelectedEvents((prev) => (checked ? [...prev, value] : prev.filter((e) => e !== value)));
  };

  const isEventEnabled = (value: string): boolean => {
    if (isEditing) return selectedEvents.includes(value);
    return rawEvents.includes(value) || (rawEvents.length === 0 && DEFAULT_CLIENT_EVENTS.includes(value as any));
  };

  const enabledCount = CLIENT_EVENTS.filter((e) => isEventEnabled(e.value)).length;

  return (
    <OverviewSectionCard
      title="Client Events"
      subtitle="Events streamed to client applications"
      genieIcon="activity"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-gray-500" />
          <Badge variant="outline">
            {enabledCount} / {CLIENT_EVENTS.length} enabled
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CLIENT_EVENTS.map((eventType) => {
            const enabled = isEventEnabled(eventType.value);
            const isDefault = DEFAULT_CLIENT_EVENTS.includes(eventType.value as any);
            return isEditing ? (
              <label
                key={eventType.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors min-h-[72px] ${
                  enabled ? "bg-blue-50 border-blue-300" : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => toggleEvent(eventType.value, e.target.checked)}
                  className="mt-0.5 mr-3 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 flex-wrap mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">{eventType.label}</span>
                    {isDefault && (
                      <Badge variant="secondary" className="text-xs flex-shrink-0">
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 leading-tight">{eventType.description}</p>
                </div>
              </label>
            ) : (
              <div
                key={eventType.value}
                className={`flex items-start p-3 rounded-lg border transition-colors ${
                  enabled ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200 opacity-60"
                }`}
              >
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${enabled ? "bg-blue-500" : "bg-gray-300"}`}
                >
                  {enabled ? <Check className="w-3 h-3 text-white" /> : <X className="w-3 h-3 text-gray-500" />}
                </div>
                <div className="min-w-0 flex-1 ml-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-sm font-medium truncate ${enabled ? "text-gray-900" : "text-gray-600"}`}>
                      {eventType.label}
                    </span>
                    {isDefault && (
                      <Badge variant="secondary" className="text-xs flex-shrink-0">
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className={`text-xs leading-tight ${enabled ? "text-gray-600" : "text-gray-500"}`}>
                    {eventType.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </OverviewSectionCard>
  );
};
